import type {
  ChatContent,
  ChatPart,
  GenerateRequest,
  GenerateResponse,
  ModelConfig,
} from './llm.js'
import {
  countSteps,
  normalizeToolInput,
  updateScenarioFunctionDeclaration,
  UPDATE_SCENARIO_TOOL_NAME,
  type Scenario,
} from './scenario.js'
import { validateScenario } from './validation.js'

export const DEFAULT_MODEL = 'gemini-3.5-flash:medium'

const MAX_TOOL_ITERATIONS = 10

export const SYSTEM_PROMPT = `あなたは手動E2Eテストのシナリオ設計アシスタントです。
ユーザーとの自然言語の対話を通じて、Chrome拡張機能「Manual E2E Test Assistant」で使うテストシナリオJSONを作成・編集します。

## 進め方
- ユーザーの説明からテスト対象・観点を整理し、${UPDATE_SCENARIO_TOOL_NAME} ツールでシナリオを作成・更新する
- 情報が足りなくても、まず妥当な叩き台を作って見せ、その後の対話で洗練させる。質問攻めにしない
- 決定的に重要な不明点（テスト対象のURL、対象機能の範囲など）だけ、叩き台を示した上で確認する
- 正常系だけでなく、異常系・境界値・エッジケースのステップも提案する

## シナリオ設計のルール
- カテゴリは機能・画面単位で分ける（例: ログイン、商品検索、カート）
- ステップは「1操作・1確認」の粒度。instruction は誰でも再現できる具体的な操作、expected は目で確認できる結果を書く
- tips には前提条件や注意点を必要な場合のみ書く
- テストに入力値が必要なステップには inputData で具体値を与える（例: メールアドレス、検索キーワード）
- ステップの実行順に前提関係がある場合のみ dependsOn を使う。循環依存は禁止

## ID規約
- すべて英小文字のケバブケース
- シナリオID: 内容を表す名前（例: "user-login-test"）
- カテゴリID: 短い機能名（例: "login", "search"）
- ステップID: 「カテゴリID-連番3桁」（例: "login-001", "login-002"）
- IDはシナリオ全体で一意。既存のIDはユーザーの指示がない限り変更しない
- version は "1.0.0" から開始する

## ツールの使い方
- シナリオに変更を加えるときは必ず ${UPDATE_SCENARIO_TOOL_NAME} を呼ぶ
- 差分ではなく、常にシナリオの完全な内容を渡す（渡した内容で全体が置き換わる）
- ツールがバリデーションエラーを返したら、修正して再度呼び出す

## 返答のスタイル
- 日本語で簡潔に。ツール実行後は「何を作った/変えたか」の要点と、次の提案や確認事項を短く伝える
- シナリオのJSONは画面の右ペインに表示されるため、チャット本文にJSONを貼らない`

const IMPORT_MESSAGE_PREFIX = '既存のシナリオJSONをインポートしました。'

const PRUNED_ARGS_NOTE =
  '過去の版のためシナリオ内容は省略済み（現在の内容は最新の update_scenario 呼び出しまたはインポートを参照）'

export interface TranscriptEntry {
  role: 'user' | 'assistant' | 'system'
  text: string
}

/** 1つの会話のチャット状態（保存形式にもそのまま使う） */
export interface ChatState {
  contents: ChatContent[]
  transcript: TranscriptEntry[]
  scenario: Scenario | null
}

export function createChatState(): ChatState {
  return { contents: [], transcript: [], scenario: null }
}

export type GenerateFn = (request: GenerateRequest) => Promise<GenerateResponse>

export interface AgentDeps {
  generate: GenerateFn
}

export interface TurnSettings {
  apiKey: string
  model: ModelConfig
}

export interface TurnResult {
  reply: string
  scenario: Scenario | null
}

interface ToolOutcome {
  ok: boolean
  message: string
}

function applyScenarioUpdate(state: ChatState, input: unknown): ToolOutcome {
  const scenario = normalizeToolInput(input)
  const result = validateScenario(scenario)

  if (!result.valid) {
    const lines = result.errors.map((e) => `- ${e.path}: ${e.message}`).join('\n')
    return {
      ok: false,
      message: `バリデーションエラーがあります。修正して再度 ${UPDATE_SCENARIO_TOOL_NAME} を呼んでください:\n${lines}`,
    }
  }

  state.scenario = scenario
  return {
    ok: true,
    message: `シナリオを更新しました（${scenario.categories.length}カテゴリ / ${countSteps(scenario)}ステップ）`,
  }
}

/** インポートしたシナリオを会話のコンテキストに反映する */
export function applyImportedScenario(state: ChatState, scenario: Scenario): void {
  state.scenario = scenario
  state.contents.push({
    role: 'user',
    parts: [
      {
        text: `${IMPORT_MESSAGE_PREFIX}これが現在のシナリオです。この内容をベースに、以降の指示に従って編集してください。\n\n\`\`\`json\n${JSON.stringify(scenario, null, 2)}\n\`\`\``,
      },
    ],
  })
  state.transcript.push({
    role: 'system',
    text: `シナリオ「${scenario.name}」をインポートしました`,
  })
}

/** 履歴中のシナリオ全文（ツール引数 or インポートJSON）の所在と、現在内容の根拠になり得るか */
interface ScenarioPayloadRef {
  contentIndex: number
  partIndex: number
  kind: 'call' | 'import'
  isTruth: boolean
}

/**
 * 過去ターンに蓄積されたシナリオ全文（update_scenario の引数・インポートJSON）を
 * プレースホルダーに置き換え、トークン消費と localStorage 使用量が会話の長さに応じて
 * 際限なく増えるのを防ぐ。
 * モデルが現在のシナリオを参照できるよう、最新のシナリオ内容（最後に成功した更新
 * またはインポート）と、直近のツール呼び出しはそのまま残す。
 * thoughtSignature を含む parts の構造は維持し、引数・本文のみ差し替える。
 */
export function compactHistory(contents: ChatContent[]): ChatContent[] {
  const refs: ScenarioPayloadRef[] = []

  for (let i = 0; i < contents.length; i++) {
    const content = contents[i]!

    if (content.role === 'user') {
      for (let p = 0; p < content.parts.length; p++) {
        const text = content.parts[p]!.text
        if (text?.startsWith(IMPORT_MESSAGE_PREFIX) && text.includes('```json')) {
          refs.push({ contentIndex: i, partIndex: p, kind: 'import', isTruth: true })
        }
      }
      continue
    }

    // model: update_scenario 呼び出しを直後の functionResponse（呼び出しと同順）と
    // 突き合わせ、成功した呼び出し＝現在のシナリオ内容の根拠として扱う
    const next = contents[i + 1]
    const responses = (next?.role === 'user' ? next.parts : []).filter((p) => p.functionResponse)
    let callOrder = 0
    for (let p = 0; p < content.parts.length; p++) {
      const call = content.parts[p]!.functionCall
      if (!call) continue
      const order = callOrder++
      if (call.name !== UPDATE_SCENARIO_TOOL_NAME) continue
      if (!call.args || 'pruned' in call.args) continue // 圧縮済み
      const response = responses[order]?.functionResponse?.response
      const succeeded = response !== undefined && !('error' in response)
      refs.push({ contentIndex: i, partIndex: p, kind: 'call', isTruth: succeeded })
    }
  }

  const keep = new Set<ScenarioPayloadRef>()
  const lastTruth = refs.filter((r) => r.isTruth).at(-1)
  if (lastTruth) keep.add(lastTruth)
  const lastCall = refs.filter((r) => r.kind === 'call').at(-1)
  if (lastCall) keep.add(lastCall)

  const prune = refs.filter((r) => !keep.has(r))
  if (prune.length === 0) return contents

  const result = [...contents]
  for (const ref of prune) {
    const content = result[ref.contentIndex]!
    const parts = [...content.parts]
    const part = parts[ref.partIndex]!
    if (ref.kind === 'call' && part.functionCall) {
      const id = part.functionCall.args['id']
      parts[ref.partIndex] = {
        ...part,
        functionCall: {
          ...part.functionCall,
          args: {
            ...(typeof id === 'string' && id ? { id } : {}),
            pruned: PRUNED_ARGS_NOTE,
          },
        },
      }
    } else {
      parts[ref.partIndex] = {
        ...part,
        text: `${IMPORT_MESSAGE_PREFIX}（インポートした内容はその後の更新で置き換えられたため省略）`,
      }
    }
    result[ref.contentIndex] = { ...content, parts }
  }
  return result
}

export async function runTurn(
  deps: AgentDeps,
  state: ChatState,
  userMessage: string,
  settings: TurnSettings,
): Promise<TurnResult> {
  state.contents = compactHistory(state.contents)
  state.contents.push({ role: 'user', parts: [{ text: userMessage }] })
  state.transcript.push({ role: 'user', text: userMessage })

  const texts: string[] = []

  for (let i = 0; i < MAX_TOOL_ITERATIONS; i++) {
    const response = await deps.generate({
      apiKey: settings.apiKey,
      model: settings.model,
      contents: [...state.contents],
      systemInstruction: SYSTEM_PROMPT,
      functionDeclarations: [updateScenarioFunctionDeclaration],
    })

    const candidate = response.candidates?.[0]
    if (!candidate?.content?.parts) {
      const reason = response.promptFeedback?.blockReason
      texts.push(
        reason
          ? `リクエストがブロックされました（${reason}）。表現を変えてもう一度お試しください。`
          : '応答を取得できませんでした。もう一度お試しください。',
      )
      break
    }

    // 思考ブロックや thoughtSignature を含む parts をそのまま履歴に残す
    // （Gemini 3系の署名維持と、Claudeのツールループ継続に必要）
    state.contents.push({ role: 'model', parts: candidate.content.parts })

    for (const part of candidate.content.parts) {
      if (part.text && !part.thought && part.text.trim()) {
        texts.push(part.text.trim())
      }
    }

    const functionCalls = candidate.content.parts.filter(
      (part): part is ChatPart & { functionCall: NonNullable<ChatPart['functionCall']> } =>
        part.functionCall !== undefined,
    )

    if (functionCalls.length > 0) {
      const responseParts: ChatPart[] = functionCalls.map((part) => {
        const outcome =
          part.functionCall.name === UPDATE_SCENARIO_TOOL_NAME
            ? applyScenarioUpdate(state, part.functionCall.args)
            : { ok: false, message: `不明なツール: ${part.functionCall.name}` }
        return {
          functionResponse: {
            name: part.functionCall.name,
            // Claude/OpenAIではツール応答を呼び出しIDで対応付ける
            ...(part.functionCall.id ? { id: part.functionCall.id } : {}),
            response: outcome.ok ? { result: outcome.message } : { error: outcome.message },
          },
        }
      })
      state.contents.push({ role: 'user', parts: responseParts })
      continue
    }

    if (candidate.finishReason === 'MAX_TOKENS') {
      texts.push('応答が長くなりすぎて途中で打ち切られました。範囲を分けて指示してください。')
    }

    break
  }

  const reply = texts.join('\n\n') || 'シナリオを更新しました。'
  state.transcript.push({ role: 'assistant', text: reply })

  return { reply, scenario: state.scenario }
}
